import { Search } from "lucide-react";
import * as React from "react";
import { cn } from "../lib/cn";
import { formatAbsolute, formatRelative, truncateMiddle } from "../lib/format";
import { Input } from "../primitives/input";
import { AppLink } from "../primitives/link";
import { Tooltip } from "../primitives/menu";
import { useNow, usePrefs } from "./prefs";

/**
 * Name cell whose link is the row's only link, stretched over the row (ui-spec §3.1, §1.3): an optional leading
 * mark, the name, and an optional muted second line; the table's own columns say what a row is.
 */
export function NameCell({
  href,
  name,
  sub,
  lead,
}: {
  href: string;
  name: string;
  sub?: React.ReactNode;
  /** A leading mark in place of the icon (the live status dot on the Runs list). */
  lead?: React.ReactNode;
}) {
  const shown = truncateMiddle(name, 48);
  return (
    <div className="flex min-w-0 items-center gap-2.5">
      {lead}
      <div className="max-w-[24rem] min-w-0">
        <AppLink
          href={href}
          data-row-link
          title={shown !== name ? name : undefined}
          className="nd-stretched block truncate text-ui text-fg-primary outline-offset-[var(--nd-focus-offset-inset)]"
        >
          {shown}
        </AppLink>
        {sub ? <div className="truncate text-small text-fg-muted">{sub}</div> : null}
      </div>
    </div>
  );
}

/** Letter tile and name; "You" for the caller (ui-spec §3.1 People cell). */
export function PersonCell({ name, you = false, via }: { name: string; you?: boolean; via?: string }) {
  const initial = (name.trim()[0] ?? "?").toUpperCase();
  return (
    <span className="inline-flex items-center gap-2">
      <span className="inline-flex size-[18px] items-center justify-center rounded-full bg-badge text-[10px] font-medium text-fg-secondary" aria-hidden>
        {initial}
      </span>
      <span>{you ? "You" : name}</span>
      {via ? <span className="text-fg-muted">via {via}</span> : null}
    </span>
  );
}

/** Relative time with the absolute time in the tooltip, in the user's timezone. */
export function TimeCell({ time }: { time: string | undefined }) {
  const now = useNow(30_000);
  const { timeZone } = usePrefs();
  if (!time) return <span className="text-fg-muted">—</span>;
  return (
    <Tooltip content={formatAbsolute(time, timeZone)}>
      <time dateTime={time} className="whitespace-nowrap">
        {formatRelative(time, now, timeZone)}
      </time>
    </Tooltip>
  );
}

/** Progress: a 96px track for running and passed rows; failed rows show only the reason caption (ui-spec §3.1). */
export function ProgressCell({
  fraction,
  caption,
  mono = false,
}: {
  fraction?: number;
  caption?: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <span className="inline-flex items-center gap-2">
      {fraction !== undefined ? (
        <span
          role="progressbar"
          aria-label={typeof caption === "string" ? caption : "Progress"}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(fraction * 100)}
          className="h-1 w-20 overflow-hidden rounded-full bg-badge"
        >
          <span className="block h-full rounded-full bg-success-bar" style={{ width: `${Math.min(1, Math.max(0, fraction)) * 100}%` }} />
        </span>
      ) : null}
      {caption ? <span className={cn("text-small text-fg-muted", mono && "font-mono")}>{caption}</span> : null}
    </span>
  );
}

/** Money in a table cell: tabular figures; ResourceTable right-aligns figure columns so places line up. */
export function MoneyCell({ children }: { children: React.ReactNode }) {
  return <span className="whitespace-nowrap tabular-nums">{children}</span>;
}

/** Row action slot: the text ActionLink and the `⋯` menu, both above the stretched link. */
export function RowActions({ children }: { children: React.ReactNode }) {
  return <div className="flex items-center justify-end gap-2">{children}</div>;
}

/** A node the list template puts at the start of the filter bar (the page's view segments). */
export const FilterBarLead = React.createContext<React.ReactNode>(null);

/**
 * Filter bar (ui-spec §3.1): the page's view segments, a search bound to `?q=` (debounced 200 ms, focused by `/`),
 * select filters, then the live indicator on the right. While rows are selected the bulk bar replaces it.
 */
export function FilterBar({
  query,
  onQueryChange,
  placeholder,
  filters,
  right,
  bulk,
}: {
  query: string;
  onQueryChange: (q: string) => void;
  placeholder: string;
  filters?: React.ReactNode;
  right?: React.ReactNode;
  bulk?: React.ReactNode;
}) {
  const lead = React.useContext(FilterBarLead);
  const [value, setValue] = React.useState(query);
  React.useEffect(() => setValue(query), [query]);
  React.useEffect(() => {
    if (value === query) return;
    const t = setTimeout(() => onQueryChange(value), 200);
    return () => clearTimeout(t);
  }, [value, query, onQueryChange]);
  if (bulk) {
    return <div className="sticky top-0 z-10 mb-3 flex h-[var(--nd-control-height)] items-center gap-4 rounded-md bg-selected px-3 text-ui">{bulk}</div>;
  }
  return (
    <div className="nd-filter-bar mb-3 flex flex-wrap items-center gap-2">
      {lead}
      <div className="nd-list-search relative w-full max-w-[320px] min-w-[min(200px,100%)] sm:w-64">
        <Search className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-fg-muted" aria-hidden />
        <Input
          type="search"
          data-page-search
          aria-label={placeholder}
          placeholder={placeholder}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="pl-8"
        />
      </div>
      {filters}
      <div className="ml-auto flex items-center gap-2">{right}</div>
    </div>
  );
}

/** "2 jobs" under the table; the timezone link only where a page offers one (ui-spec §3.1 footer). */
export function ListFooter({ count, timezoneHref }: { count: React.ReactNode; timezoneHref?: string }) {
  const { timeZone } = usePrefs();
  return (
    <div className="mt-2 flex items-center justify-between text-small text-fg-muted">
      <span>{count}</span>
      {timezoneHref ? (
        <AppLink href={timezoneHref} className="hover:text-fg-primary">
          {timeZone}
        </AppLink>
      ) : null}
    </div>
  );
}
